import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit3, X, Save, BookOpen, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCES = ['PubMed (NIH NLM)', 'PubChem (NIH NCBI)', 'DrugBank', 'NIH ClinicalTrials.gov', 'WHO INN', 'Other'];

export default function AdminReferencesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [refs, setRefs] = useState([]);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ product_id: '', title: '', url: '', source: 'PubMed (NIH NLM)', description: '', sort_order: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (user && user.role !== 'admin') navigate('/'); }, [user, navigate]);

  const fetchAll = async () => {
    try {
      const [{ data: p }, { data: r }] = await Promise.all([
        axios.get(`${API}/admin/products`, { withCredentials: true }),
        axios.get(`${API}/admin/references`, { withCredentials: true }),
      ]);
      setProducts((p.products || []).filter((x) => (x.product_types || []).includes('peptide')));
      setRefs(r.references || []);
    } catch {
      toast.error('Failed to load references');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetchAll(); }, [user]);

  const grouped = useMemo(() => {
    const out = {};
    for (const p of products) {
      out[p.id] = { product: p, refs: [] };
    }
    for (const r of refs) {
      if (!out[r.product_id]) continue;
      out[r.product_id].refs.push(r);
    }
    Object.values(out).forEach((g) => g.refs.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
    let entries = Object.values(out);
    if (filter.trim()) {
      const q = filter.toLowerCase();
      entries = entries.filter((g) => g.product.name.toLowerCase().includes(q) || g.product.id.includes(q));
    }
    return entries.sort((a, b) => a.product.name.localeCompare(b.product.name));
  }, [products, refs, filter]);

  const startCreate = (productId) => {
    setForm({ product_id: productId, title: '', url: '', source: 'PubMed (NIH NLM)', description: '', sort_order: 0 });
    setCreating(true);
    setEditing(null);
  };
  const startEdit = (r) => {
    setForm({ ...r });
    setEditing(r.id);
    setCreating(false);
  };
  const cancel = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!form.title.trim() || !form.url.trim() || !form.product_id) { toast.error('Title, URL, and product are required'); return; }
    try {
      if (creating) {
        await axios.post(`${API}/admin/references`, form, { withCredentials: true });
        toast.success('Reference added');
      } else {
        const { id: _id, product_id: _pid, created_at: _ca, ...patch } = form;
        await axios.patch(`${API}/admin/references/${editing}`, patch, { withCredentials: true });
        toast.success('Reference updated');
      }
      cancel();
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reference?')) return;
    try {
      await axios.delete(`${API}/admin/references/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="admin-references-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl tracking-tight font-light text-foreground font-heading flex items-center gap-2"><BookOpen className="h-6 w-6" /> Reference Library</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">Curate peer-reviewed sources for every peptide. Visible to customers on the product detail page and on /references.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input placeholder="Search peptides…" value={filter} onChange={(e) => setFilter(e.target.value)} className="w-64" data-testid="references-search-input" />
        </div>
      </div>

      {(creating || editing) && (
        <Card className="mb-6 border-[hsl(210,45%,40%)]/40">
          <CardHeader><CardTitle className="font-heading text-lg">{creating ? `New reference for ${form.product_id}` : `Edit reference`}</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Retatrutide — peer-reviewed studies" data-testid="ref-title-field" /></div>
            <div className="space-y-1 sm:col-span-2"><Label className="text-xs">URL</Label><Input value={form.url} onChange={(e) => setForm((p) => ({ ...p, url: e.target.value }))} placeholder="https://pubmed.ncbi.nlm.nih.gov/?term=..." data-testid="ref-url-field" /></div>
            <div className="space-y-1">
              <Label className="text-xs">Source</Label>
              <Select value={form.source} onValueChange={(v) => setForm((p) => ({ ...p, source: v }))}>
                <SelectTrigger data-testid="ref-source-field"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))} data-testid="ref-sort-field" /></div>
            <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="Curated peer-reviewed research from the U.S. National Library of Medicine." data-testid="ref-description-field" /></div>
            <div className="flex gap-2 sm:col-span-2">
              <Button onClick={save} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid="save-ref-button"><Save className="h-4 w-4 mr-1" /> Save</Button>
              <Button variant="outline" onClick={cancel}><X className="h-4 w-4 mr-1" /> Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading…</p>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ product, refs: prefs }) => (
            <Card key={product.id} data-testid={`refs-group-${product.id}`}>
              <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
                <CardTitle className="font-heading text-lg">{product.name} <span className="text-xs text-muted-foreground font-mono">({product.id})</span></CardTitle>
                <Button size="sm" onClick={() => startCreate(product.id)} className="bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white" data-testid={`add-ref-${product.id}`}><Plus className="h-3 w-3 mr-1" /> Add reference</Button>
              </CardHeader>
              <CardContent className="pt-0">
                {prefs.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No references yet for this peptide.</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {prefs.map((r) => (
                      <li key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`ref-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[hsl(210,45%,40%)]">{r.source}</p>
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-foreground hover:underline">{r.title} <ExternalLink className="inline h-3 w-3 ml-0.5 opacity-60" /></a>
                          {r.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{r.description}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-ref-${r.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(r.id)} data-testid={`delete-ref-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
